import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { TypedMemo } from '../../../common/types';
import { SMLRouteNames, SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { ArtistSwitcher, ArtistSwitcherSheetControl } from '../../../accountSelector/ArtistSwitcher';
import { SMLLink } from '../../components/SMLLink/SMLLink';
import { Music24Logo } from '../../components/Music24Logo';
import { Music24FullLogo } from '../../components/Music24FullLogo';
import { openCommandPalette } from '../../components/CommandPalette/commandPaletteStore';
import { findNavItemForScreen, smlHomeNavItem } from '../smlNavigation';
import { smlNavigationChromeMessages as messages, smlNavigationMessages } from '../smlNavigationMessages';
import { useAccountIdentity } from '../account/useAccountIdentity';
import { AccountArt } from '../account/AccountArt';
import styles from './MobileTopBar.module.scss';

interface MobileTopBarProps {
  screen: SMLRouteNames | undefined;
  isAccountOpen: boolean;
  accountSheetId: string;
  onOpenAccount: () => void;
  /** design: navigation i1. The pill opens the switcher sheet. Absent on a Discover page. */
  artistSheet?: ArtistSwitcherSheetControl;
  /** The social accounts that need a new sign-in: the red pip on the avatar. */
  accountAttention: number;
  params?: Record<string, string>;
}

/**
 * design: navigation-v2 7a, 7c. The logo (goes Home) and the page title (the menu item of the
 * page; the full logo on Home), the artist pill (every page but Discover), the
 * search button (the command palette, full screen on a phone) and the account avatar with the
 * Connections pip, which opens the account sheet.
 */
const MobileTopBarComponent: React.FC<MobileTopBarProps> = ({
  screen,
  isAccountOpen,
  accountSheetId,
  onOpenAccount,
  artistSheet,
  accountAttention,
  params,
}) => {
  const intl = useIntl();
  const identity = useAccountIdentity();
  const item = findNavItemForScreen(screen);
  // Home and a page with no menu item show the word mark; every other page shows its title.
  const title =
    screen === SMLRouteNames.browse
      ? intl.formatMessage(messages.search)
      : item === undefined || item.id === smlHomeNavItem.id
        ? undefined
        : intl.formatMessage(item.label);

  return (
    <header className={styles.bar}>
      <SMLLink
        to={SMLRoutes.home}
        params={params}
        className={title === undefined ? styles.wordmark : styles.logo}
        ariaLabel={intl.formatMessage(smlNavigationMessages.home)}
      >
        {title === undefined ? <Music24FullLogo size={22} /> : <Music24Logo size={24} />}
      </SMLLink>
      <span className={styles.title}>{title}</span>
      {artistSheet !== undefined && (
        <div className={styles.switcher}>
          <ArtistSwitcher variant="compact" sheet={artistSheet} />
        </div>
      )}
      <button
        type="button"
        className={styles.roundButton}
        onClick={openCommandPalette}
        aria-label={intl.formatMessage(messages.search)}
        aria-haspopup="dialog"
        aria-current={screen === SMLRouteNames.browse ? 'page' : undefined}
      >
        <FontAwesomeIcon icon={faMagnifyingGlass} />
      </button>
      <button
        type="button"
        className={styles.avatar}
        onClick={onOpenAccount}
        aria-label={intl.formatMessage(messages.account)}
        aria-haspopup="dialog"
        aria-expanded={isAccountOpen}
        aria-controls={isAccountOpen ? accountSheetId : undefined}
      >
        <AccountArt
          name={identity.name}
          avatarUrl={identity.avatarUrl}
          hasAttention={accountAttention > 0}
          size="top"
        />
      </button>
    </header>
  );
};

export const MobileTopBar = TypedMemo(MobileTopBarComponent);
